import { useEffect, useState, useRef } from "react";
import {
  Download, Upload, Database, Loader2, ShieldAlert, CheckCircle2,
  FileArchive, HardDriveDownload, History, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";

const modeInfo = {
  merge: { label: "Tambahkan data / Merge", desc: "Menambahkan data baru saja. Data yang sudah ada (ID sama) dilewati. Aman - tidak menimpa.", danger: false },
  replace: { label: "Replace data dengan ID sama", desc: "Menambahkan data baru dan memperbarui data dengan ID yang sama. Data lain tetap ada.", danger: true },
  wipe: { label: "Hapus data lama lalu restore penuh", desc: "MENGHAPUS seluruh data pada setiap koleksi lalu memulihkan dari backup. Berisiko!", danger: true },
};

const AdminBackup = () => {
  const { refresh } = useSite();
  const [info, setInfo] = useState({ counts: {}, media_count: 0, logs: [] });
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("merge");
  const [report, setReport] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileRef = useRef();
  useSeo("Database & Backup");

  const loadInfo = () => api.get("/admin/backup/info").then((r) => setInfo(r.data)).catch(() => {});
  useEffect(() => { loadInfo(); }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/backup/export", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `website-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Export database berhasil diunduh");
      loadInfo();
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Akses khusus Super Admin" : "Gagal export database");
    } finally {
      setExporting(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setPreview(null); setReport(null);
    if (f && !f.name.endsWith(".zip")) { toast.error("File harus berformat .zip"); return; }
    setSelectedFile(f || null);
  };

  const analyze = async () => {
    if (!selectedFile) { toast.error("Pilih file backup (.zip) terlebih dahulu"); return; }
    setAnalyzing(true);
    try {
      const form = new FormData();
      form.append("file", selectedFile);
      form.append("mode", mode);
      form.append("dry_run", "true");
      const { data } = await api.post("/admin/backup/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data.manifest);
      toast.success("Backup valid & siap di-import");
    } catch (e) {
      toast.error(e.response?.data?.detail || "File backup tidak valid");
    } finally {
      setAnalyzing(false);
    }
  };

  const runImport = async () => {
    setConfirmOpen(false);
    setImporting(true);
    setReport(null);
    try {
      const form = new FormData();
      form.append("file", selectedFile);
      form.append("mode", mode);
      form.append("dry_run", "false");
      const { data } = await api.post("/admin/backup/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data.report);
      toast.success("Import database selesai");
      loadInfo(); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal import database");
    } finally {
      setImporting(false);
    }
  };

  const totalRecords = Object.values(info.counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-slate-900"><Database className="h-6 w-6 text-emerald-600" /> Database & Backup</h1>
        <p className="mt-1 text-sm text-slate-500">Export seluruh database & media ke file ZIP, atau restore dari backup. Khusus Super Admin.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* EXPORT */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><HardDriveDownload className="h-5 w-5" /></div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-800">Export Database</h2>
              <p className="text-sm text-slate-500">Backup seluruh data teks + gambar/media</p>
            </div>
          </div>
          <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
            <div className="flex justify-between py-1"><span>Total record database</span><span className="font-semibold text-slate-800">{totalRecords}</span></div>
            <div className="flex justify-between py-1"><span>File media</span><span className="font-semibold text-slate-800">{info.media_count}</span></div>
            <div className="flex justify-between py-1"><span>Koleksi</span><span className="font-semibold text-slate-800">{Object.keys(info.counts || {}).length}</span></div>
          </div>
          <Button onClick={doExport} disabled={exporting} className="mt-4 w-full gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="backup-export-button">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {exporting ? "Menyiapkan ZIP..." : "Export Database"}
          </Button>
        </div>

        {/* IMPORT */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><Upload className="h-5 w-5" /></div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-slate-800">Import / Restore Database</h2>
              <p className="text-sm text-slate-500">Restore dari file backup (.zip)</p>
            </div>
          </div>

          <button type="button" onClick={() => fileRef.current?.click()} data-testid="backup-choose-file"
            className="mt-4 flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 text-left transition-colors hover:border-emerald-300">
            <FileArchive className="h-6 w-6 shrink-0 text-slate-400" />
            <span className="min-w-0 flex-1 truncate text-sm text-slate-600">{selectedFile ? selectedFile.name : "Pilih File ZIP backup..."}</span>
          </button>
          <input ref={fileRef} type="file" accept=".zip" onChange={onFile} className="hidden" data-testid="backup-file-input" />

          <Button onClick={analyze} disabled={!selectedFile || analyzing} variant="outline" className="mt-3 w-full gap-2 border-emerald-200 text-emerald-700" data-testid="backup-analyze-button">
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Analisis Backup
          </Button>
        </div>
      </div>

      {/* PREVIEW + MODE + IMPORT */}
      {preview && (
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6" data-testid="backup-preview">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-slate-800"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Backup Ditemukan</h3>
          <div className="mt-3 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
            <p>Sekolah: <span className="font-medium text-slate-800">{preview.school_name}</span></p>
            <p>Dibuat: <span className="font-medium text-slate-800">{formatDate(preview.created_at)}</span></p>
            <p>Versi backup: <span className="font-medium text-slate-800">{preview.backup_version}</span></p>
            <p>Jumlah media: <span className="font-medium text-slate-800">{preview.media_count}</span></p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(preview.tables || {}).map(([k, v]) => (
              <span key={k} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">{k}: {v}</span>
            ))}
          </div>

          <div className="mt-5">
            <p className="mb-2 text-sm font-semibold text-slate-700">Mode Import:</p>
            <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
              {Object.entries(modeInfo).map(([key, m]) => (
                <label key={key} htmlFor={`mode-${key}`} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${mode === key ? "border-emerald-400 bg-white" : "border-slate-200 bg-white/60"}`}>
                  <RadioGroupItem value={key} id={`mode-${key}`} data-testid={`backup-mode-${key}`} className="mt-0.5" />
                  <div>
                    <p className={`text-sm font-medium ${m.danger ? "text-red-700" : "text-slate-800"}`}>{m.label}{m.danger && <AlertTriangle className="ml-1 inline h-3.5 w-3.5" />}</p>
                    <p className="text-xs text-slate-500">{m.desc}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>

          <Button onClick={() => (mode === "merge" ? runImport() : setConfirmOpen(true))} disabled={importing} className="mt-4 w-full gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="backup-import-button">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {importing ? "Sedang mengimport..." : "Import Database"}
          </Button>
          {importing && <p className="mt-2 text-center text-xs text-slate-500">Memproses data & mengupload media, mohon tunggu...</p>}
        </div>
      )}

      {/* REPORT */}
      {report && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="backup-report">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Import Berhasil</h3>
          <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr><th className="px-4 py-2 text-left font-medium">Koleksi</th><th className="px-3 py-2 text-center font-medium">Baru</th><th className="px-3 py-2 text-center font-medium">Diperbarui</th><th className="px-3 py-2 text-center font-medium">Dilewati</th><th className="px-3 py-2 text-center font-medium">Error</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.entries(report).map(([k, v]) => (
                  <tr key={k}>
                    <td className="px-4 py-2 font-medium text-slate-700">{k}{v.note && <span className="block text-xs font-normal text-amber-600">{v.note}</span>}</td>
                    <td className="px-3 py-2 text-center text-emerald-700">{v.created ?? v.uploaded ?? "-"}</td>
                    <td className="px-3 py-2 text-center text-blue-600">{v.updated ?? "-"}</td>
                    <td className="px-3 py-2 text-center text-slate-400">{v.skipped ?? "-"}</td>
                    <td className={`px-3 py-2 text-center ${(v.errors || v.failed) ? "text-red-600" : "text-slate-400"}`}>{v.errors ?? v.failed ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* LOGS */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-slate-800"><History className="h-5 w-5 text-slate-400" /> Log Aktivitas</h3>
        {(info.logs || []).length === 0 ? (
          <p className="mt-3 text-sm text-slate-400">Belum ada aktivitas export/import.</p>
        ) : (
          <div className="mt-4 divide-y divide-slate-100">
            {info.logs.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-4 py-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-slate-700">{l.action === "export" ? "Export" : "Import"} {l.mode ? `(${l.mode})` : ""} <span className="font-normal text-slate-400">— {l.by}</span></p>
                  <p className="truncate text-xs text-slate-400">{l.filename}</p>
                </div>
                <div className="shrink-0 text-right">
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">{l.status}</span>
                  <p className="mt-1 text-xs text-slate-400">{formatDate(l.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Danger confirm */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="backup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700"><ShieldAlert className="h-5 w-5" /> Konfirmasi Import ({modeInfo[mode].label})</AlertDialogTitle>
            <AlertDialogDescription>{modeInfo[mode].desc} Pastikan Anda memiliki backup terbaru sebelum melanjutkan. Tindakan ini dapat memengaruhi data existing.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={runImport} className="bg-red-600 hover:bg-red-700" data-testid="backup-confirm-import">Ya, Lanjutkan Import</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBackup;
